import EmberService, { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { PopupData, PopupListItem, Service } from 'webapp-types';
import {
	BillingFeature,
	ReminderObjectType,
	ScheduleType,
	ScorecardType,
} from 'webapp/enums/enums';
import {
	NotificationCreateType,
	NotificationFilterFrequency,
	NotificationFilterType,
} from 'webapp/enums/notifications';
import { TrackingEvent } from 'webapp/enums/tracking';

import NotificationsEditPopupComponent from 'webapp/components/notifications/edit/popup';
import NotificationsPopupCreateChoiceComponent from 'webapp/components/notifications/popup-create-choice';

import type { Notification } from 'webapp/models-custom';
import Alert from 'webapp/models-custom/alert';
import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import BoardShare from 'webapp/models-custom/board-share';
import Goal from 'webapp/models-custom/goal';
import MetricAnomaly from 'webapp/models-custom/metric-anomaly';
import MetricSetup from 'webapp/models-custom/metric-setup';
import NotificationAlert from 'webapp/models-custom/notification/alert';
import NotificationBoardShare from 'webapp/models-custom/notification/board-share';
import NotificationDigest from 'webapp/models-custom/notification/digest';
import NotificationGoal from 'webapp/models-custom/notification/goal';
import NotificationMetricAnomaly from 'webapp/models-custom/notification/metric-anomaly';
import NotificationReminder from 'webapp/models-custom/notification/reminder';
import NotificationReportScheduleSetting from 'webapp/models-custom/notification/report-schedule-setting';
import NotificationScorecard from 'webapp/models-custom/notification/scorecard';
import Reminder from 'webapp/models-custom/reminder';
import Report from 'webapp/models-custom/report';
import ReportScheduleSetting from 'webapp/models-custom/report-schedule-setting';
import Scorecard from 'webapp/models-custom/scorecard';

import type { TrackingOriginForCreateOrEdit as AlertsTrackingOriginForCreateOrEdit } from 'webapp/services/model-manager/alerts';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TrackingOriginForCreateOrEdit as DigestsTrackingOriginForCreateOrEdit } from 'webapp/services/model-manager/metric-digests';
import type { TrackingOriginTypeForSchedulePopup as ReportScheduleSettingTrackingOriginForCreateOrEdit } from 'webapp/services/model-manager/reports';
import type { TrackingOriginForCreateOrEdit as ScorecardsTrackingOriginForCreateOrEdit } from 'webapp/services/model-manager/scorecards';

export type ModelWithNotification =
	Scorecard | BoardShare | ReportScheduleSetting | Reminder | Alert | MetricAnomaly | Goal;

export type ModelWithNotificationForPopup = Exclude<ModelWithNotification, Goal>;

export default class ModelManagerNotificationsService extends EmberService {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;
	@service('model-manager/reminders') declare mmRemindersService: Service.ModelManager.Reminders;
	@service('model-manager/alerts') declare mmAlertsService: Service.ModelManager.Alerts;
	@service('model-manager/metric-anomalies')
	declare mmMetricAnomaliesService: Service.ModelManager.MetricAnomalies;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/metric-digests')
	declare mmMetricDigestsService: Service.ModelManager.MetricDigests;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;

	// ------ Model creation ------

	@cached
	get hasAnyRecordsWithNotifications() {
		return !!this.getAllRecordsWithPossibleNotifications().length;
	}

	getAllRecordsWithPossibleNotifications() {
		return [
			...this.mmScorecardsService.savedScorecardRecords,
			...this.mmMetricDigestsService.savedMetricDigestRecords,
			...this.mmBoardSharesService.savedRecords,
			...this.mmReportScheduleSettingsService.savedRecords,
			...this.mmRemindersService.savedRecords,
			...this.mmAlertsService.savedRecords,
			...this.mmMetricAnomaliesService.savedRecords,
			...this.mmGoalsService.savedRecords,
		];
	}

	getAllModelsWithPossibleNotifications(): ModelWithNotification[] {
		return [
			...this.mmScorecardsService.scorecardModels,
			...this.mmMetricDigestsService.metricDigestModels,
			...this.mmBoardSharesService.scheduledBoardShareModels,
			...this.mmReportScheduleSettingsService.getAllModels(),
			...this.mmRemindersService.getAllModels(),
			...this.mmAlertsService.getAllModels(),
			...this.mmMetricAnomaliesService.metricAnomalies,
			...this.mmGoalsService.goalForSpaceModels,
		];
	}

	createFromModel(model: ModelWithNotification) {
		let notification: Notification | null = null;
		if (model instanceof Scorecard) {
			if (model.isDigest) {
				notification = new NotificationDigest(model);
			} else {
				notification = new NotificationScorecard(model);
			}
		} else if (model instanceof BoardShare) {
			notification = new NotificationBoardShare(model);
		} else if (model instanceof ReportScheduleSetting) {
			notification = new NotificationReportScheduleSetting(model);
		} else if (model instanceof Reminder) {
			notification = new NotificationReminder(model);
		} else if (model instanceof Alert) {
			notification = new NotificationAlert(model);
		} else if (model instanceof MetricAnomaly) {
			notification = new NotificationMetricAnomaly(model);
		} else if (model instanceof Goal) {
			notification = new NotificationGoal(model);
		}
		return notification;
	}

	createAndEdit(type: NotificationCreateType) {
		const origin = 'notifications';
		switch (type) {
			case NotificationCreateType.METRIC:
				this.mmMetricDigestsService.createAndEdit(origin);
				break;
			case NotificationCreateType.BOARD:
				this.mmBoardSharesService.createNewAndOpenEdit(origin);
				break;
			case NotificationCreateType.REPORT:
				this.mmReportScheduleSettingsService.createNewAndOpenEdit(origin);
				break;
			case NotificationCreateType.REMINDER:
				this.mmRemindersService.createAndEdit(origin);
				break;
			case NotificationCreateType.SCORECARD:
				this.mmScorecardsService.createAndEdit(origin);
				break;
			case NotificationCreateType.ALERT:
				this.mmAlertsService.createAndEdit(origin);
				break;
		}
	}

	createAndEditForTypeAndMetricSetup(
		type: NotificationCreateType,
		metricSetup: MetricSetup,
		trackingOrigin: string,
		isFromCreateChoicePopupFlow = false,
	) {
		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}
		if (type === NotificationCreateType.METRIC) {
			this.mmMetricDigestsService.createAndEditForMetricSetup(
				metricSetup,
				trackingOrigin as DigestsTrackingOriginForCreateOrEdit,
				isFromCreateChoicePopupFlow,
			);
		} else if (type === NotificationCreateType.SCORECARD) {
			this.mmScorecardsService.createAndEditForMetricSetup(
				metricSetup,
				trackingOrigin as ScorecardsTrackingOriginForCreateOrEdit,
				isFromCreateChoicePopupFlow,
			);
		} else if (type === NotificationCreateType.ALERT) {
			this.mmAlertsService.createAndEditForMetricSetup(
				metricSetup,
				trackingOrigin as AlertsTrackingOriginForCreateOrEdit,
				isFromCreateChoicePopupFlow,
			);
		} else if (type === NotificationCreateType.SMART_ALERT) {
			const existingAnomaly =
				this.mmMetricAnomaliesService.getMetricAnomalyAlertForMetricSetup(metricSetup);
			const model =
				existingAnomaly ||
				this.mmMetricAnomaliesService.createModelForMetricSetup(metricSetup);
			this.mmMetricAnomaliesService.edit(model, trackingOrigin, isFromCreateChoicePopupFlow);
		}
	}

	createNewForFeature(
		type: NotificationCreateType,
		item: MetricSetup | Board | BoardLoop | Report,
		trackingOrigin: string,
		isFromCreateChoicePopupFlow = false,
	) {
		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}
		if (item instanceof MetricSetup) {
			this.createAndEditForTypeAndMetricSetup(
				type,
				item,
				trackingOrigin,
				isFromCreateChoicePopupFlow,
			);
		} else if (item instanceof Board) {
			if (type === NotificationCreateType.BOARD) {
				this.mmBoardSharesService.createAndEditForBoard(
					item,
					trackingOrigin,
					undefined,
					isFromCreateChoicePopupFlow,
				);
			}
		} else if (item instanceof BoardLoop) {
			if (type === NotificationCreateType.BOARD) {
				this.mmBoardSharesService.createAndEditForLoop(
					item,
					trackingOrigin,
					undefined,
					isFromCreateChoicePopupFlow,
				);
			}
		} else if (item instanceof Report) {
			if (type === NotificationCreateType.REPORT) {
				this.mmReportScheduleSettingsService.createAndEditForReport(
					item,
					trackingOrigin as ReportScheduleSettingTrackingOriginForCreateOrEdit,
					isFromCreateChoicePopupFlow,
				);
			} else if (type === NotificationCreateType.REMINDER) {
				this.mmRemindersService.createAndEditForReport(
					item,
					trackingOrigin,
					isFromCreateChoicePopupFlow,
				);
			}
		}
	}

	getModelForTypeAndId(
		type: NotificationCreateType,
		id: string,
	): ModelWithNotificationForPopup | null {
		switch (type) {
			case NotificationCreateType.METRIC:
				return this.mmMetricDigestsService.findModel(id);
			case NotificationCreateType.SCORECARD:
				return this.mmScorecardsService.findModel(id);
			case NotificationCreateType.SMART_ALERT:
				return this.mmMetricAnomaliesService.findModel(id);
			case NotificationCreateType.ALERT:
				return this.mmAlertsService.findModel(id);
			case NotificationCreateType.REPORT:
				return this.mmReportScheduleSettingsService.findModel(id);
			case NotificationCreateType.REMINDER:
				return this.mmRemindersService.findModel(id);
			case NotificationCreateType.BOARD:
				return this.mmBoardSharesService.findModel(id);
			default:
				return null;
		}
	}

	getMetricSetupForModel(model: ModelWithNotificationForPopup): MetricSetup | null {
		if (model instanceof Scorecard) {
			return (
				(model.temporaryEditDataSelects[0] || model.savedDataSelects[0])?.metricSetup ??
				null
			);
		}
		if (model instanceof Alert || model instanceof MetricAnomaly) {
			return model.metricSetup;
		}
		return null;
	}

	// Only one smart alert can exist per metric setup.
	metricSetupHasExistingSmartAlert(model: ModelWithNotificationForPopup): boolean {
		const metricSetup = this.getMetricSetupForModel(model);
		return (
			!!metricSetup &&
			this.mmMetricAnomaliesService.getIsMetricAnomalyAlertEnabledForMetricSetup(metricSetup)
		);
	}

	createOtherCreateTypeModelFromModel(
		type: NotificationCreateType,
		model: ModelWithNotificationForPopup,
	) {
		switch (type) {
			case NotificationCreateType.METRIC:
			case NotificationCreateType.SCORECARD:
			case NotificationCreateType.SMART_ALERT:
			case NotificationCreateType.ALERT: {
				const origin = 'notifications';
				let metricSetup = this.getMetricSetupForModel(model);
				if (!metricSetup) {
					metricSetup = new MetricSetup();
				}
				switch (type) {
					case NotificationCreateType.METRIC:
						return this.mmMetricDigestsService.createModelForMetricSetup(
							metricSetup,
							origin,
						);
					case NotificationCreateType.SCORECARD:
						return this.mmScorecardsService.createModelForMetricSetup(
							metricSetup,
							origin,
						);
					case NotificationCreateType.SMART_ALERT:
						return this.mmMetricAnomaliesService.createModelForMetricSetup(metricSetup);
					case NotificationCreateType.ALERT:
						return this.mmAlertsService.createModelForMetricSetup(metricSetup, origin);
				}
			}
			case NotificationCreateType.REPORT: {
				let report: Report | null = null;
				if (model instanceof ReportScheduleSetting || model instanceof Reminder) {
					report = model.report;
				}
				if (report) {
					return this.mmReportScheduleSettingsService.createModelForReport(report);
				} else {
					return this.mmReportScheduleSettingsService.createNewModel();
				}
			}
			case NotificationCreateType.REMINDER: {
				let report: Report | null = null;
				if (model instanceof ReportScheduleSetting || model instanceof Reminder) {
					report = model.report;
				}
				if (report) {
					return this.mmRemindersService.createModelForReport(report);
				} else {
					return this.mmRemindersService.createNewModel({
						objectType: ReminderObjectType.REPORT,
					});
				}
			}
			case NotificationCreateType.BOARD: {
				let boardOrLoop: Board | BoardLoop | null = null;
				if (model instanceof BoardShare) {
					boardOrLoop = model.target;
				}
				if (boardOrLoop instanceof BoardLoop) {
					return this.mmBoardSharesService.createModelForLoop(boardOrLoop);
				} else if (boardOrLoop instanceof Board) {
					return this.mmBoardSharesService.createModelForBoard(boardOrLoop);
				} else {
					return this.mmBoardSharesService.createNewModel();
				}
			}
		}
	}

	checkIfModelMatchesForList(
		formItem: ModelWithNotificationForPopup,
		listItem: ModelWithNotificationForPopup,
	) {
		if (
			formItem instanceof ReportScheduleSetting &&
			listItem instanceof ReportScheduleSetting
		) {
			return formItem.reportId === listItem.reportId;
		} else if (formItem instanceof Reminder && listItem instanceof Reminder) {
			return (
				formItem.objectType === listItem.objectType &&
				formItem.objectId === listItem.objectId
			);
		} else if (formItem instanceof BoardShare && listItem instanceof BoardShare) {
			if (formItem.isLoop && listItem.isLoop) {
				return formItem.loopId === listItem.loopId;
			} else if (!formItem.isLoop && !listItem.isLoop) {
				return formItem.boardId === listItem.boardId;
			}
		} else if (formItem instanceof Scorecard && listItem instanceof Scorecard) {
			if (formItem.isDigest && listItem.isDigest) {
				return this.checkIfMetricSetupMatchesForList(
					formItem.temporaryEditDataSelects[0]?.metricSetup,
					listItem.savedDataSelects[0]?.metricSetup,
				);
			} else {
				// no list for scorecards
				return false;
			}
		} else if (formItem instanceof Alert && listItem instanceof Alert) {
			return this.checkIfMetricSetupMatchesForList(
				formItem.metricSetup,
				listItem.metricSetup,
			);
		} else if (formItem instanceof MetricAnomaly && listItem instanceof MetricAnomaly) {
			return this.checkIfMetricSetupMatchesForList(
				formItem.metricSetup,
				listItem.metricSetup,
			);
		}
		return false;
	}

	checkIfMetricSetupMatchesForList(formMetricSetup?: MetricSetup, listMetricSetup?: MetricSetup) {
		if (!formMetricSetup || !listMetricSetup) {
			return false;
		}
		// rules: if form has only metric, any list items with that metric match (dimensions / filters are irrelevant)
		// if form has metric + dimensions / filters, the list items have to match them
		// example 1: form: metric X + dimension None matches any list items w/ metric X (whichever dimensions / filters)
		// example 2: form: metric X + dimension M + dim value A matches only list items with exactly that setup
		if (formMetricSetup.key) {
			if (formMetricSetup.key === listMetricSetup.key) {
				if (formMetricSetup.filterGroups.length) {
					return (
						formMetricSetup.isMatchingDimensionsFromMetricSetup(listMetricSetup) &&
						formMetricSetup.filterGroups.length ===
							listMetricSetup.filterGroups.length &&
						formMetricSetup.isMatchingFiltersFromMetricSetup(listMetricSetup)
					);
				} else if (formMetricSetup.dimensions.length) {
					return formMetricSetup.isMatchingDimensionsFromMetricSetup(listMetricSetup);
				} else {
					return true; // no filters and dimensions in form - all with same metric match
				}
			}
		} else {
			return true; // no metric in form - all match
		}
		return false;
	}

	startCreateFlow(origin: string) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}

		const popup = this.popupService.createModal(NotificationsPopupCreateChoiceComponent, {
			popupClass: 'popup-notification-create-choice',
			allowStacked: true,
		});
		popup.open({
			origin,
		});

		this.analyticsService.event(TrackingEvent.NEW_NOTIFICATION, {
			origin: 'notifications',
		});

		return popup;
	}

	openEditPopup(
		model: ModelWithNotificationForPopup,
		origin: string,
		isFromCreateChoicePopupFlow = false,
		extraProps: Omit<
			PopupData.Notification.Edit,
			'model' | 'origin' | 'isFromCreateChoicePopupFlow'
		> = {},
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}

		// make a copy
		if (!model.isNew) {
			model = model.createAnotherModel();
		}

		const popup = this.popupService.createModal(NotificationsEditPopupComponent, {
			popupClass: 'popup-notification-edit',
			allowStacked: true,
		});
		popup.open(Object.assign({ model, origin, isFromCreateChoicePopupFlow }, extraProps));
		return popup;
	}

	openEditPopupWithTransition(model: ModelWithNotificationForPopup) {
		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}

		// origin always notifications
		const createType = this.getCreateTypeForModel(model);
		this.navigationService.transitionToRoute('settings.notifications.edit', createType, model);
	}

	confirmAndDeleteNotification(model: Notification) {
		return new Promise<void>((resolve, reject) => {
			if (this.accountService.handleDemo()) {
				reject();
				return;
			}

			this.popupService.openConfirmPopup({
				onConfirm: () => {
					model.delete();
					this.infolayerService.ok('Notification deleted!');
					resolve();
				},
				onReject: () => {
					reject();
				},
				title: 'Are you sure you want to remove this notification?',
				text: '',
				confirmAction: 'Remove',
				cancelAction: 'Cancel',
			});
		});
	}

	// ----- Duplicating logic -----
	canDuplicateModel(model: ModelWithNotification) {
		return !(model instanceof Goal) && !(model instanceof MetricAnomaly);
	}

	createDuplicatedModel(model: ModelWithNotification) {
		// goals and anomalies have no duplicate option
		if (!this.canDuplicateModel(model)) {
			return null;
		}

		let copy: ModelWithNotification | null = null;
		if (model instanceof Scorecard) {
			copy = model.isDigest
				? this.mmMetricDigestsService.createDuplicatedModel(model)
				: this.mmScorecardsService.createDuplicatedModel(model);
		} else if (model instanceof BoardShare) {
			copy = this.mmBoardSharesService.createDuplicatedModel(model);
		} else if (model instanceof ReportScheduleSetting) {
			copy = this.mmReportScheduleSettingsService.createDuplicatedModel(model);
		} else if (model instanceof Reminder) {
			copy = this.mmRemindersService.createDuplicatedModel(model);
		} else if (model instanceof Alert) {
			copy = this.mmAlertsService.createDuplicatedModel(model);
		}

		return copy;
	}

	// ----- Per-feature show / create notifications -----

	private readonly notificationTypeBillingFeatureMap: Partial<
		Record<NotificationCreateType, BillingFeature>
	> = {
		[NotificationCreateType.ALERT]: BillingFeature.ALERTS,
		[NotificationCreateType.SMART_ALERT]: BillingFeature.SMART_ALERTS,
		[NotificationCreateType.REPORT]: BillingFeature.REPORTS_BASIC,
		[NotificationCreateType.REMINDER]: BillingFeature.REPORTS_BASIC,
	};

	getNotificationsDropdownOptionForFeature<Action>(
		item: MetricSetup | Board | BoardLoop | Report,
		action: Action,
	): PopupListItem<Action> | null {
		if (this.currentUserService.isViewer) {
			return null;
		}
		const count = this.getNotificationTypesWithCountForFeature(item);

		let availableTypes: NotificationCreateType[] = [];

		if (item instanceof MetricSetup) {
			availableTypes.push(
				NotificationCreateType.METRIC,
				NotificationCreateType.SCORECARD,
				NotificationCreateType.SMART_ALERT,
				NotificationCreateType.ALERT,
			);
		} else if (item instanceof Report) {
			availableTypes.push(NotificationCreateType.REPORT, NotificationCreateType.REMINDER);
		} else if (item instanceof Board || item instanceof BoardLoop) {
			if (!item.isAccountTemplate) {
				availableTypes.push(NotificationCreateType.BOARD);
			}
		}

		availableTypes = availableTypes.filter((id) => {
			return !this.checkLimitationForCreateType(id);
		});

		const sumCount = Object.values(count).reduce((sum, n) => sum + n, 0);

		if (!availableTypes.length) {
			return null;
		} else {
			return {
				action,
				id: availableTypes[0],
				text: 'Notifications',
				icon: 'Bell',
				used: sumCount,
				forceShowUsed: true,
			};
		}
	}

	getNameForCreateType(type: NotificationCreateType): string {
		switch (type) {
			case NotificationCreateType.METRIC:
				return 'Weekly update';
			case NotificationCreateType.SCORECARD:
				return 'Scorecard';
			case NotificationCreateType.SMART_ALERT:
				return 'Smart alert';
			case NotificationCreateType.ALERT:
				return 'Manual alert';
			case NotificationCreateType.REPORT:
				return 'Scheduled report';
			case NotificationCreateType.REMINDER:
				return 'Reminder';
			case NotificationCreateType.BOARD:
				return 'Scheduled snapshot';
			default:
				return '';
		}
	}

	getNotificationTypesWithCountForFeature(
		item: MetricSetup | Board | BoardLoop | Report,
	): Partial<Record<NotificationCreateType, number>> {
		const matchingModels: ModelWithNotification[] = [];

		if (item instanceof MetricSetup) {
			for (const scorecard of this.mmScorecardsService.scorecardModels) {
				if (
					scorecard.savedDataSelects.some((ds) =>
						ds.metricSetup?.isMatchingMetricSetup(item),
					)
				) {
					matchingModels.push(scorecard);
				}
			}

			for (const digest of this.mmMetricDigestsService.metricDigestModels) {
				if (
					digest.savedDataSelects.some((ds) =>
						ds.metricSetup?.isMatchingMetricSetup(item),
					)
				) {
					matchingModels.push(digest);
				}
			}

			for (const alert of this.mmAlertsService.getAllModels()) {
				if (alert.metricSetup?.isMatchingMetricSetup(item)) {
					matchingModels.push(alert);
				}
			}

			const anomaly = this.mmMetricAnomaliesService.getMetricAnomalyAlertForMetricSetup(item);

			if (anomaly) {
				matchingModels.push(anomaly);
			}
		} else if (item instanceof Board || item instanceof BoardLoop) {
			for (const boardShare of this.mmBoardSharesService.scheduledBoardShareModels) {
				if (item instanceof Board && boardShare.boardId === item.numericId) {
					matchingModels.push(boardShare);
				} else if (item instanceof BoardLoop && boardShare.loopId === item.numericId) {
					matchingModels.push(boardShare);
				}
			}
		} else if (item instanceof Report) {
			matchingModels.push(
				...this.mmReportScheduleSettingsService.getForReport(item.numericId!),
				...this.mmRemindersService.getForReport(item.numericId!),
			);
		}

		const counts: Partial<Record<NotificationCreateType, number>> = {};

		for (const model of matchingModels) {
			const notification = this.createFromModel(model);

			if (notification?.isValidForList) {
				const createType = this.getCreateTypeForModel(model);

				if (createType) {
					counts[createType] = (counts[createType] || 0) + 1;
				}
			}
		}

		return counts;
	}

	getCreateTypeForModel(model: ModelWithNotification): NotificationCreateType | null {
		if (model instanceof Scorecard) {
			return model.type === ScorecardType.METRIC_DIGEST
				? NotificationCreateType.METRIC
				: NotificationCreateType.SCORECARD;
		}
		if (model instanceof Alert) return NotificationCreateType.ALERT;
		if (model instanceof MetricAnomaly) return NotificationCreateType.SMART_ALERT;
		if (model instanceof BoardShare) return NotificationCreateType.BOARD;
		if (model instanceof ReportScheduleSetting) return NotificationCreateType.REPORT;
		if (model instanceof Reminder) return NotificationCreateType.REMINDER;
		return null;
	}

	// ------ Helper methods ------

	getFrequencyFromSchedule(scheduleType: ScheduleType) {
		switch (scheduleType) {
			case ScheduleType.DAILY:
				return NotificationFilterFrequency.DAILY;
			case ScheduleType.WEEKLY:
				return NotificationFilterFrequency.WEEKLY;
			case ScheduleType.MONTHLY:
				return NotificationFilterFrequency.MONTHLY;
			case ScheduleType.QUARTERLY:
				return NotificationFilterFrequency.QUARTERLY;
			default:
				return null; // hourly, yearly
		}
	}

	getFrequencyText(frequency: NotificationFilterFrequency) {
		switch (frequency) {
			case NotificationFilterFrequency.DAILY:
				return 'Daily';
			case NotificationFilterFrequency.WEEKLY:
				return 'Weekly';
			case NotificationFilterFrequency.BIWEEKLY:
				return 'Bi-Weekly';
			case NotificationFilterFrequency.MONTHLY:
				return 'Monthly';
			case NotificationFilterFrequency.QUARTERLY:
				return 'Quarterly';
			case NotificationFilterFrequency.CONDITIONAL:
				return 'On condition met';
			case NotificationFilterFrequency.STATUS_CHANGE:
				return 'On status change';
			case NotificationFilterFrequency.ANOMALY:
				return 'Anomaly';
		}
	}

	getTypeText(type: NotificationFilterType) {
		switch (type) {
			case NotificationFilterType.METRIC:
				return 'Metric';
			case NotificationFilterType.SCORECARD:
				return 'Scorecard';
			case NotificationFilterType.ALERT:
				return 'Alert';
			case NotificationFilterType.BOARD:
				return 'Dashboard';
			case NotificationFilterType.LOOP:
				return 'Loop';
			case NotificationFilterType.REPORT:
				return 'Report';
			case NotificationFilterType.GOAL:
				return 'Goal';
		}
		return '';
	}

	checkLimitationForCreateType(type: NotificationCreateType) {
		const billingFeature = this.notificationTypeBillingFeatureMap[type];
		if (billingFeature) {
			if (this.billingService.checkIfCan(billingFeature)) {
				return null;
			} else {
				return billingFeature;
			}
		}
		return null;
	}

	updateModelCache(cached: Notification[], freshRecords: ModelWithNotification[]): boolean {
		return ModelManagerForStoreService.updateModelCacheHelper<
			ModelWithNotification,
			Notification
		>(cached, freshRecords, (item: ModelWithNotification) => {
			return this.createFromModel(item)!;
		});
	}

	// ------ Requests ------

	requestAllTargetsForNotifications() {
		return Promise.allSettled([
			this.requestNotificationsForFeature('metric'),
			this.requestNotificationsForFeature('board'),
			this.requestNotificationsForFeature('report'),
			this.requestNotificationsForFeature('goal'),
		]);
	}

	requestNotificationsForFeature(
		featureType: 'metric' | 'board' | 'loop' | 'report' | 'goal',
	): Promise<unknown> {
		const promises: Promise<unknown>[] = [];
		if (featureType === 'metric') {
			promises.push(
				this.mmScorecardsService.requestScorecards(),
				this.mmAlertsService.requestAlerts(),
				this.mmMetricAnomaliesService.getMetricAnomalies(),
			);
		} else if (featureType === 'board' || featureType === 'loop') {
			promises.push(this.mmBoardSharesService.requestBoardShares());
		} else if (featureType === 'report') {
			promises.push(
				this.mmReportScheduleSettingsService.requestReportSchedules(),
				this.mmRemindersService.requestReminders(),
			);
		} else if (featureType === 'goal') {
			promises.push(this.mmGoalsService.requestSpaceGoals());
		}
		return Promise.allSettled(promises);
	}

	requestConnectedModelsForNotifications(onlyIfNotificationsExist = false) {
		const promises: Promise<unknown>[] = [];

		// we might need to load these items even if we have no records of this type
		// because when adding new notifications, we'll need them

		if (!onlyIfNotificationsExist || this.mmBoardSharesService.savedRecords.length) {
			promises.push(this.mmBoardsService.requestBoards());
			promises.push(this.mmBoardLoopsService.requestBoardLoops());
		}
		if (
			!onlyIfNotificationsExist ||
			this.mmReportScheduleSettingsService.savedRecords.length ||
			this.mmRemindersService.savedRecords.length
		) {
			promises.push(this.mmReportsService.requestReports());
		}
		if (!onlyIfNotificationsExist || this.mmMetricAnomaliesService.savedRecords.length) {
			promises.push(this.mmMetricsService.requestMetrics());
		}

		return Promise.allSettled(promises);
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/notifications': ModelManagerNotificationsService;
	}
}
